import React, { useMemo, useState, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Cell, ResponsiveContainer,
} from "recharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AppLayout, PageHeader } from "./_shared/AppLayout";
import { brl, fmtDate, sumValor, fmtMonthLong } from "./_shared/utils";
import { catById } from "./_shared/categories";
import { useAppData } from "./_shared/store";
import LancamentoModal from "./_shared/LancamentoModal";
import ConfirmDialog from "./_shared/ConfirmDialog";
import {
  Plus, Pencil, Trash2, Search, Download,
  FileSpreadsheet, FileText, TrendingUp, TrendingDown,
  Minus, Trophy,
} from "lucide-react";
import { toast } from "sonner";

const MESES_CURTOS = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const PAGAMENTOS   = ["Pix","Débito","Cartão","Dinheiro","Boleto","Transferência","Corretora","Outro"];

/* ── Export dropdown ─────────────────────────────────────── */
function ExportMenu({ onXLS, onPDF }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="h-11 px-4 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:text-white hover:border-slate-600 transition text-sm font-semibold inline-flex items-center gap-2"
      >
        <Download className="h-4 w-4" /> Exportar
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-44 bg-slate-800 border border-slate-700 rounded-xl overflow-hidden z-20 shadow-2xl">
            <button
              onClick={() => { onXLS(); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-slate-200 hover:bg-slate-700 transition"
            >
              <FileSpreadsheet className="h-4 w-4 text-emerald-400" />
              Excel (.xlsx)
            </button>
            <button
              onClick={() => { onPDF(); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-slate-200 hover:bg-slate-700 transition border-t border-slate-700"
            >
              <FileText className="h-4 w-4 text-rose-400" />
              PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Main ────────────────────────────────────────────────── */
export default function GastosVariaveis() {
  const { data, updateLancamento, removeLancamento, removeLancamentoSerie } = useAppData();
  const ym         = data.selectedMonth;
  const year       = ym.slice(0, 4);
  const all        = data.gastosVariaveis || [];
  const customCats = data.customCategories || [];

  /* ── Filter state ── */
  const [q,            setQ]            = useState("");
  const [filterCat,    setFilterCat]    = useState("all");
  const [filterPag,    setFilterPag]    = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  /* ── Modal state ── */
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [confirmDel, setConfirmDel] = useState({ open: false, id: null, desc: "", seriesId: null });

  /* ── Current month items ── */
  const monthItems = useMemo(
    () => all.filter((i) => (i.data || "").startsWith(ym)),
    [all, ym],
  );

  /* ── Previous month ── */
  const prevYM = useMemo(() => {
    const [y, m] = ym.split("-").map(Number);
    return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
  }, [ym]);

  const prevTotal = useMemo(
    () => sumValor(all.filter((i) => (i.data || "").startsWith(prevYM))),
    [all, prevYM],
  );

  /* ── Totals ── */
  const totalMes      = sumValor(monthItems);
  const totalPago     = sumValor(monthItems.filter((i) =>  i.pago));
  const totalPendente = sumValor(monthItems.filter((i) => !i.pago));

  /* ── Comparativo ── */
  const comparativo = useMemo(() => {
    if (!prevTotal) return null;
    const diff = totalMes - prevTotal;
    const pct  = Math.abs((diff / prevTotal) * 100).toFixed(1);
    if (Math.abs(diff) < 0.01) return { type: "equal", text: "Mesmo valor do mês passado" };
    if (diff > 0) return { type: "up",   text: `Esse mês você gastou ${brl(diff)} a mais que o mês passado (▲ ${pct}%)` };
    return          { type: "down", text: `Esse mês você gastou ${brl(Math.abs(diff))} a menos que o mês passado (▼ ${pct}%)` };
  }, [totalMes, prevTotal]);

  /* ── Filtered table rows ── */
  const filtered = useMemo(
    () =>
      monthItems
        .filter((i) => i.desc.toLowerCase().includes(q.toLowerCase()))
        .filter((i) => filterCat    === "all" || i.cat       === filterCat)
        .filter((i) => filterPag    === "all" || i.pagamento === filterPag)
        .filter((i) => filterStatus === "all" || (filterStatus === "pago" ? i.pago : !i.pago))
        .sort((a, b) => (a.data || "").localeCompare(b.data || "")),
    [monthItems, q, filterCat, filterPag, filterStatus],
  );

  /* ── Category cards ── */
  const catSummaries = useMemo(() => {
    const ids = [...new Set(monthItems.map((i) => i.cat).filter(Boolean))];
    return ids
      .map((catId) => ({
        ...catById(catId, customCats),
        total: sumValor(monthItems.filter((i) => i.cat === catId)),
      }))
      .filter((c) => c.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [monthItems, customCats]);

  /* ── Top-3 ranking ── */
  const catRanking = useMemo(
    () => catSummaries.slice(0, 3).map((c) => ({
      ...c,
      pct: totalMes > 0 ? (c.total / totalMes) * 100 : 0,
    })),
    [catSummaries, totalMes],
  );

  /* ── 12-month chart data ── */
  const chartData = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const m   = String(i + 1).padStart(2, "0");
        const ymM = `${year}-${m}`;
        const its = all.filter((it) => (it.data || "").startsWith(ymM));
        const byCat = {};
        its.forEach((it) => { byCat[it.cat] = (byCat[it.cat] || 0) + Number(it.valor || 0); });
        return {
          mes: MESES_CURTOS[i],
          ym:  ymM,
          total: sumValor(its),
          topCats: Object.entries(byCat).sort((a, b) => b[1] - a[1]).slice(0, 4),
        };
      }),
    [all, year],
  );

  /* ── Categories in use (filter dropdown) ── */
  const catsInUse = useMemo(() => {
    const ids = [...new Set(all.map((i) => i.cat).filter(Boolean))];
    return ids.map((id) => catById(id, customCats)).filter(Boolean);
  }, [all, customCats]);

  /* ── Custom chart tooltip ── */
  const ChartTooltip = useCallback(({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    if (!d.total) return null;
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-3 shadow-2xl min-w-[160px]">
        <div className="text-sm font-bold text-white mb-2">{d.mes} · {brl(d.total)}</div>
        {d.topCats.map(([catId, val]) => {
          const cat = catById(catId, customCats);
          return (
            <div key={catId} className="flex items-center justify-between gap-4 text-xs mt-1">
              <span className="flex items-center gap-1.5 text-slate-300">
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: cat.color }} />
                {cat.name}
              </span>
              <span className="font-semibold text-white tabular-nums">{brl(val)}</span>
            </div>
          );
        })}
      </div>
    );
  }, [customCats]);

  /* ── Handlers ── */
  const togglePago  = (id, cur)       => updateLancamento("variavel", id, { pago: !cur });
  const askRemove   = (id, desc, sid) => setConfirmDel({ open: true, id, desc, seriesId: sid || null });
  const openCreate  = ()              => { setEditing(null); setModalOpen(true); };
  const openEdit    = (it)            => { setEditing(it);   setModalOpen(true); };

  const doRemove = () => {
    if (confirmDel.id) {
      removeLancamento("variavel", confirmDel.id);
      toast.success(`"${confirmDel.desc || "Lançamento"}" removido`);
    }
  };
  const doRemoveSerie = () => {
    if (confirmDel.seriesId) {
      removeLancamentoSerie("variavel", confirmDel.seriesId);
      toast.success(`Série "${confirmDel.desc}" removida`);
    }
  };

  /* ── Export ── */
  const exportXLS = () => {
    const rows = filtered.map((it) => ({
      "Descrição":   it.desc,
      "Categoria":   catById(it.cat, customCats).name,
      "Pagamento":   it.pagamento || "",
      "Data":        fmtDate(it.data),
      "Status":      it.pago ? "Pago" : "Pendente",
      "Valor (R$)":  Number(it.valor),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Gastos Variáveis");
    XLSX.writeFile(wb, `gastos-variaveis-${ym}.xlsx`);
    toast.success("Exportado para Excel!");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Gastos Variáveis — ${fmtMonthLong(ym)}`, 14, 15);
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.text(`Total: ${brl(totalMes)}  |  ${filtered.length} lançamentos`, 14, 22);
    autoTable(doc, {
      head: [["Descrição", "Categoria", "Pagamento", "Data", "Status", "Valor"]],
      body: filtered.map((it) => [
        it.desc,
        catById(it.cat, customCats).name,
        it.pagamento || "",
        fmtDate(it.data),
        it.pago ? "Pago" : "Pendente",
        brl(it.valor),
      ]),
      startY: 27,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [15, 23, 42], textColor: [148, 163, 184] },
      alternateRowStyles: { fillColor: [30, 41, 59] },
      bodyStyles: { fillColor: [17, 24, 39], textColor: [226, 232, 240] },
    });
    doc.save(`gastos-variaveis-${ym}.pdf`);
    toast.success("Exportado para PDF!");
  };

  /* ── Render ── */
  return (
    <AppLayout>
      <PageHeader title="Gastos Variáveis" subtitle={fmtMonthLong(ym)} testid="variaveis" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-6">

        {/* ── TOP BAR ── */}
        <div className="rounded-2xl p-4 md:p-5 bg-slate-900 border border-slate-800" data-testid="variaveis-summary">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Total do mês</div>
              <div className="mt-1 font-display text-2xl md:text-3xl font-bold text-white">{brl(totalMes)}</div>
              {totalPendente > 0 && (
                <div className="text-xs text-slate-400 mt-0.5">
                  <span className="text-emerald-400">{brl(totalPago)}</span>{" pago · "}
                  <span className="text-yellow-400">{brl(totalPendente)}</span>{" pendente"}
                </div>
              )}
              {comparativo && (
                <div className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${
                  comparativo.type === "up"    ? "text-rose-400"    :
                  comparativo.type === "down"  ? "text-emerald-400" : "text-slate-400"
                }`}>
                  {comparativo.type === "up"   && <TrendingUp   className="h-3.5 w-3.5 shrink-0" />}
                  {comparativo.type === "down" && <TrendingDown  className="h-3.5 w-3.5 shrink-0" />}
                  {comparativo.type === "equal"&& <Minus         className="h-3.5 w-3.5 shrink-0" />}
                  <span>{comparativo.text}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0 flex-wrap">
              <ExportMenu onXLS={exportXLS} onPDF={exportPDF} />
              <button
                onClick={openCreate}
                className="monyx-btn-primary text-white rounded-full h-11 px-5 text-sm font-semibold inline-flex items-center justify-center gap-2"
                data-testid="variaveis-add"
              >
                <Plus className="h-4 w-4" /> Novo lançamento
              </button>
            </div>
          </div>
        </div>

        {/* ── CATEGORY CARDS ── */}
        {catSummaries.length > 0 && (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3"
            data-testid="variaveis-cat-cards"
          >
            {catSummaries.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setFilterCat((v) => (v === cat.id ? "all" : cat.id))}
                className={`rounded-xl p-3 text-left border transition ${
                  filterCat === cat.id
                    ? "border-slate-500 bg-slate-800"
                    : "bg-slate-900 border-slate-800 hover:border-slate-600"
                }`}
                style={{ borderTop: `3px solid ${cat.color}` }}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold truncate">{cat.name}</div>
                <div className="mt-1 text-sm font-display font-bold text-white tabular-nums">{brl(cat.total)}</div>
              </button>
            ))}
          </div>
        )}

        {/* ── RANKING + CHART ── */}
        {(catRanking.length > 0 || chartData.some((d) => d.total > 0)) && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

            {/* Ranking */}
            {catRanking.length > 0 && (
              <div className="lg:col-span-2 rounded-2xl bg-slate-900 border border-slate-800 p-4 md:p-5">
                <h3 className="font-display text-base font-bold mb-4 flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-yellow-400" />
                  Onde você mais gastou
                </h3>
                <div className="space-y-4">
                  {catRanking.map((cat, idx) => (
                    <div key={cat.id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-bold text-slate-500">#{idx + 1}</span>
                          <span className="text-sm font-semibold text-white truncate">{cat.name}</span>
                        </div>
                        <div className="shrink-0 text-right ml-3">
                          <div className="text-sm font-bold text-white tabular-nums">{brl(cat.total)}</div>
                          <div className="text-[11px] text-slate-400">{cat.pct.toFixed(1)}%</div>
                        </div>
                      </div>
                      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${cat.pct}%`, background: cat.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Chart */}
            <div className={`${catRanking.length > 0 ? "lg:col-span-3" : "lg:col-span-5"} rounded-2xl bg-slate-900 border border-slate-800 p-4 md:p-5`}>
              <h3 className="font-display text-base font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-400" />
                Evolução {year}
              </h3>
              <div style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                    <XAxis
                      dataKey="mes"
                      tick={{ fill: "#94a3b8", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#94a3b8", fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                      width={34}
                    />
                    <Tooltip content={ChartTooltip} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                    <Bar dataKey="total" radius={[4, 4, 0, 0]} maxBarSize={40}>
                      {chartData.map((entry) => (
                        <Cell
                          key={entry.ym}
                          fill={entry.ym === ym ? "#f59e0b" : entry.total > 0 ? "#3b82f6" : "#1e293b"}
                          fillOpacity={entry.total > 0 ? 1 : 0.3}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span className="h-2.5 w-2.5 rounded-sm bg-amber-400 shrink-0" /> Mês atual
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span className="h-2.5 w-2.5 rounded-sm bg-blue-500 shrink-0" /> Outros meses
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── FILTERS ── */}
        <div className="flex flex-wrap gap-3 items-center" data-testid="variaveis-filters">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              placeholder="Buscar por descrição..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full h-10 pl-9 pr-4 rounded-full bg-slate-900 border border-slate-800 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/40"
              data-testid="variaveis-search"
            />
          </div>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="h-10 rounded-full bg-slate-900 border border-slate-800 px-4 text-sm text-slate-300 focus:outline-none focus:border-emerald-500/40"
          >
            <option value="all">Todas categorias</option>
            {catsInUse.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            value={filterPag}
            onChange={(e) => setFilterPag(e.target.value)}
            className="h-10 rounded-full bg-slate-900 border border-slate-800 px-4 text-sm text-slate-300 focus:outline-none focus:border-emerald-500/40"
          >
            <option value="all">Todos pagamentos</option>
            {PAGAMENTOS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-10 rounded-full bg-slate-900 border border-slate-800 px-4 text-sm text-slate-300 focus:outline-none focus:border-emerald-500/40"
          >
            <option value="all">Todos status</option>
            <option value="pago">✅ Pago</option>
            <option value="pendente">⏳ Pendente</option>
          </select>
          {(q || filterCat !== "all" || filterPag !== "all" || filterStatus !== "all") && (
            <button
              onClick={() => { setQ(""); setFilterCat("all"); setFilterPag("all"); setFilterStatus("all"); }}
              className="h-10 px-4 rounded-full bg-slate-800 border border-slate-700 text-xs text-slate-400 hover:text-white transition"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {/* ── TABLE ── */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden" data-testid="variaveis-table">
          {/* Header desktop */}
          <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-800">
            <div className="col-span-4">Descrição</div>
            <div className="col-span-2">Categoria</div>
            <div className="col-span-2">Pagamento</div>
            <div className="col-span-1">Data</div>
            <div className="col-span-1 text-center">Status</div>
            <div className="col-span-1 text-right">Valor</div>
            <div className="col-span-1 text-right">Ações</div>
          </div>

          <div className="divide-y divide-slate-800">
            {filtered.length === 0 ? (
              <div className="px-5 py-10 text-center text-slate-400 text-sm">
                Nenhum lançamento encontrado. Clique em <b>Novo lançamento</b> para começar.
              </div>
            ) : (
              filtered.map((it) => {
                const cat = catById(it.cat, customCats);
                return (
                  <div
                    key={it.id}
                    className={`px-4 md:px-5 py-3 hover:bg-slate-900/60 transition ${!it.pago ? "border-l-2 border-yellow-500/30" : ""}`}
                  >
                    {/* Desktop */}
                    <div className="hidden md:grid grid-cols-12 items-center text-sm">
                      <div className="col-span-4 flex items-center gap-3 min-w-0">
                        <button
                          onClick={() => togglePago(it.id, it.pago)}
                          className={`h-5 w-5 rounded-full border-2 grid place-items-center shrink-0 transition ${
                            it.pago
                              ? "bg-emerald-500 border-emerald-500"
                              : "border-slate-600 bg-transparent hover:border-emerald-400/60"
                          }`}
                          data-testid={`row-${it.id}-pago`}
                          title={it.pago ? "Marcar como pendente" : "Marcar como pago"}
                        >
                          {it.pago && (
                            <svg viewBox="0 0 12 10" className="h-3 w-3">
                              <path d="M1 5 L4.5 8.5 L11 1" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
                            </svg>
                          )}
                        </button>
                        <div className="min-w-0">
                          <div className="text-white truncate">{it.desc}</div>
                          {it.nota && <div className="text-[11px] text-slate-500 truncate italic">{it.nota}</div>}
                        </div>
                      </div>

                      <div className="col-span-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cat.bg} ${cat.text}`}>
                          <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: cat.color }} />
                          {cat.name}
                        </span>
                      </div>

                      <div className="col-span-2 text-slate-300 text-xs">{it.pagamento}</div>
                      <div className="col-span-1 text-slate-400 text-xs tabular-nums">{fmtDate(it.data)}</div>

                      <div className="col-span-1 text-center">
                        {it.pago
                          ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400 font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10">✅ Pago</span>
                          : <span className="inline-flex items-center gap-1 text-xs text-yellow-400 font-semibold px-2 py-0.5 rounded-full bg-yellow-500/10">⏳ Pendente</span>
                        }
                      </div>

                      <div className="col-span-1 text-right font-semibold tabular-nums text-white">{brl(it.valor)}</div>

                      <div className="col-span-1 flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(it)}
                          className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-300 hover:text-emerald-300 transition"
                          data-testid={`row-${it.id}-edit`}
                          title="Editar"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => askRemove(it.id, it.desc, it.seriesId)}
                          className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-300 hover:text-rose-300 transition"
                          data-testid={`row-${it.id}-del`}
                          title="Remover"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Mobile */}
                    <div className="md:hidden flex flex-col gap-2.5">
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => togglePago(it.id, it.pago)}
                          className={`h-5 w-5 mt-0.5 rounded-full border-2 grid place-items-center shrink-0 transition ${
                            it.pago ? "bg-emerald-500 border-emerald-500" : "border-slate-600 bg-transparent"
                          }`}
                          data-testid={`row-${it.id}-pago-m`}
                        >
                          {it.pago && (
                            <svg viewBox="0 0 12 10" className="h-3 w-3">
                              <path d="M1 5 L4.5 8.5 L11 1" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
                            </svg>
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <div className="text-white font-medium truncate">{it.desc}</div>
                            <div className="shrink-0 font-bold tabular-nums text-white">{brl(it.valor)}</div>
                          </div>
                          {it.nota && <div className="text-[11px] text-slate-500 italic mt-0.5">{it.nota}</div>}
                          <div className="mt-0.5 text-[11px] text-slate-400">
                            {it.pago ? "✅ Pago" : "⏳ Pendente"} · {fmtDate(it.data)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between flex-wrap gap-2 pl-8">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cat.bg} ${cat.text}`}>
                            <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: cat.color }} />
                            {cat.name}
                          </span>
                          <span className="text-[11px] text-slate-300 px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700">{it.pagamento}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEdit(it)}
                            className="h-8 w-8 grid place-items-center rounded-lg bg-slate-800/70 text-slate-300 hover:text-emerald-300 transition"
                            data-testid={`row-${it.id}-edit-m`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => askRemove(it.id, it.desc, it.seriesId)}
                            className="h-8 w-8 grid place-items-center rounded-lg bg-slate-800/70 text-slate-300 hover:text-rose-300 transition"
                            data-testid={`row-${it.id}-del-m`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer row */}
          {filtered.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-800 flex items-center justify-between text-sm">
              <span className="text-slate-400">{filtered.length} lançamento{filtered.length !== 1 ? "s" : ""}</span>
              <span className="font-bold text-white tabular-nums">{brl(sumValor(filtered))}</span>
            </div>
          )}
        </div>

      </div>

      <LancamentoModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        mode={editing ? "edit" : "create"}
        tipo="variavel"
        item={editing}
        defaultMonth={ym}
      />

      <ConfirmDialog
        open={confirmDel.open}
        onOpenChange={(v) => setConfirmDel((s) => ({ ...s, open: v }))}
        title="Remover lançamento?"
        description={`Deseja remover "${confirmDel.desc}"?${confirmDel.seriesId ? " Este lançamento faz parte de uma série recorrente." : ""}`}
        confirmLabel="Remover"
        onConfirm={doRemove}
        seriesLabel={confirmDel.seriesId ? "Remover toda a série" : undefined}
        onConfirmSeries={confirmDel.seriesId ? doRemoveSerie : undefined}
      />
    </AppLayout>
  );
}
